// src/pages/financeiro/RecebimentoConsultaPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaFileExcel, FaFilePdf, FaSortDown, FaSortUp, FaTrashAlt } from 'react-icons/fa';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useRecebimento } from '../../contexts/financeiro/RecebimentoContext';
import { useCliente } from '../../contexts/pessoas/ClienteContext';
import { useFormaPag } from '../../contexts/financeiro/FormaPagContext';
import { usePrinter } from '../../contexts/geral/PrinterContext';
import { formatCurrency } from '../../utils/numberUtils';
import { formatDate } from '../../utils/dateTimeUtils';
import Input from '../../components/input/Input';
import Select from '../../components/input/Select';
import PageContainer from '../../components/geral/PageContainer';
import PageHeader from '../../components/geral/PageHeader';
import PageFooter from '../../components/geral/PageFooter';
import PageContent from '../../components/geral/PageContent';
import RecebimentoNotasModal from './RecebimentoNotasModal';
import RecebimentoCad from './RecebimentoCad';
import RecebimentoDetalhesModal from './RecebimentoDetalhesModal';
import RecebimentoExclusaoModal from './RecebimentoExclusaoModal';
import { buscarComprovanteRecebimento, buscarDetalhesRecebimento } from '../../services/financeiro/RecebimentoService';
import { STATUS_RECEBIMENTO, obterClasseStatusRecebimento, obterDescricaoStatusRecebimento } from '../../models/financeiro/Recebimento';
import './Recebimento.css';
import {
    gerarRelatorioContasReceberExcel,
    gerarRelatorioContasReceberPdf,
} from '../../services/geral/RelatorioGerencialService';
import {
    baixarBlob,
    criarNomeArquivo,
    exibirPdfBlob,
    fecharJanela,
    prepararJanelaPdf,
} from '../../utils/ArquivoUtils';

// Função auxiliar para calcular dias de atraso
const formatarDataInput = (data) => {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
};

const subtrairMeses = (data, quantidadeMeses) => {
    const dataCalculada = new Date(data.getFullYear(), data.getMonth() - quantidadeMeses, 1);
    const ultimoDiaMes = new Date(
        dataCalculada.getFullYear(),
        dataCalculada.getMonth() + 1,
        0,
    ).getDate();
    dataCalculada.setDate(Math.min(data.getDate(), ultimoDiaMes));
    return dataCalculada;
};

const hoje = new Date();
const periodoInicialPadrao = formatarDataInput(subtrairMeses(hoje, 6));
const periodoFinalPadrao = formatarDataInput(hoje);
const obterDescricaoDocumento = (documento) => documento.numero
    ? `${documento.modeloDescricao || 'Documento'} ${documento.numero}`
    : String(documento.notaId);
const obterDescricaoDocumentos = (recebimento) => {
    const documentos = recebimento.documentos || [];
    return documentos.length ? documentos.map(obterDescricaoDocumento).join(' / ') : '-';
};

const RecebimentoConsultaPage = () => {
    const { search } = useLocation();
    const parametrosDashboard = useMemo(() => {
        const parametros = new URLSearchParams(search);
        if (parametros.get('origem') !== 'dashFinanceiro') return null;
        return {
            filialId: Number(parametros.get('filialId')) || null,
            clienteId: Number(parametros.get('clienteId')) || '',
            clienteNome: parametros.get('clienteNome') || '',
        };
    }, [search]);
    const { recebimentos = [], buscarRecebimentos: consultarRecebimentos, excluirRecebimento } = useRecebimento();
    const { clientes, showClienteSearch } = useCliente();
    const { formasPag } = useFormaPag();
    const { showMessage } = useMessage();
    const { imprimirPdfBlob } = usePrinter();
    const [selecionados, setSelecionados] = useState([]);
    const [recebimentoDetalhesId, setRecebimentoDetalhesId] = useState(null);
    const [recebimentoNotasId, setRecebimentoNotasId] = useState(null);
    const [showRecebimentoCad, setShowRecebimentoCad] = useState(false);
    const [recebimentoExclusao, setRecebimentoExclusao] = useState(null);
    const [gerandoRelatorio, setGerandoRelatorio] = useState(null);

    // Parâmetros de pesquisa
    const [periodoInicial, setPeriodoInicial] = useState(parametrosDashboard ? '' : periodoInicialPadrao);
    const [periodoFinal, setPeriodoFinal] = useState(parametrosDashboard ? '' : periodoFinalPadrao);
    const [clienteId, setClienteId] = useState(parametrosDashboard?.clienteId || '');
    const [clienteNome, setClienteNome] = useState(parametrosDashboard?.clienteNome || '');
    const [statusRec, setStatusRec] = useState(STATUS_RECEBIMENTO.NAO_PAGO);
    const [formaPagId, setFormaPagId] = useState('');
    const [numeroDocumento, setNumeroDocumento] = useState('');
    const [ordenacao, setOrdenacao] = useState({ campo: 'dataCriacao', direcao: 'desc' });

    const filtrosAtuais = () => ({
        ...(parametrosDashboard?.filialId ? { filialId: parametrosDashboard.filialId } : {}),
        dataCriIni: periodoInicial ? `${periodoInicial}T00:00:00` : null,
        dataCriFim: periodoFinal ? `${periodoFinal}T00:00:00` : null,
        clienteId: clienteId || null,
        clienteNome: clienteNome || null,
        statusRec,
        formaPagId: formaPagId || null,
        numeroDocumento: numeroDocumento || null,
    });
    const buscarRecebimentos = async (limparSelecao = true) => {
        try {
            await consultarRecebimentos(filtrosAtuais());
            if (limparSelecao) setSelecionados([]);
        } catch (error) {
            showMessage(`Erro ao buscar recebimentos: ${error?.message || error}`, 'danger');
        }
    };

    const gerarRelatorio = async (formato) => {
        let janelaPdf = null;
        try {
            setGerandoRelatorio(formato);
            if (formato === 'pdf') janelaPdf = prepararJanelaPdf();
            const filtros = {
                ...filtrosAtuais(),
                ordenarPor: ordenacao.campo,
                ordemDescendente: ordenacao.direcao === 'desc',
            };
            const arquivo = formato === 'pdf'
                ? await gerarRelatorioContasReceberPdf(filtros)
                : await gerarRelatorioContasReceberExcel(filtros);
            if (formato === 'pdf') {
                exibirPdfBlob(arquivo, janelaPdf);
            } else {
                baixarBlob(arquivo, criarNomeArquivo('relatorio-contas-receber', 'xlsx'));
            }
        } catch (error) {
            fecharJanela(janelaPdf);
            showMessage(error?.message || 'Não foi possível gerar o relatório de contas a receber.', 'danger');
        } finally {
            setGerandoRelatorio(null);
        }
    };

    useEffect(() => {
        const origemDashboard = !!parametrosDashboard;
        setPeriodoInicial(origemDashboard ? '' : periodoInicialPadrao);
        setPeriodoFinal(origemDashboard ? '' : periodoFinalPadrao);
        setClienteId(parametrosDashboard?.clienteId || '');
        setClienteNome(parametrosDashboard?.clienteNome || '');
        setStatusRec(STATUS_RECEBIMENTO.NAO_PAGO);
        setFormaPagId('');
        setNumeroDocumento('');
        setSelecionados([]);
        consultarRecebimentos({
            dataCriIni: origemDashboard ? null : `${periodoInicialPadrao}T00:00:00`,
            dataCriFim: origemDashboard ? null : `${periodoFinalPadrao}T00:00:00`,
            clienteId: parametrosDashboard?.clienteId || null,
            clienteNome: null,
            statusRec: STATUS_RECEBIMENTO.NAO_PAGO,
            formaPagId: null,
            numeroDocumento: null,
            ...(parametrosDashboard?.filialId ? { filialId: parametrosDashboard.filialId } : {}),
        }).catch((error) => showMessage(`Erro ao buscar recebimentos: ${error?.message || error}`, 'danger'));
    }, [search, consultarRecebimentos]);

    const selecionarCliente = (cliente) => {
        setClienteId(cliente?.id || '');
        setClienteNome(cliente?.pessoa?.nome || cliente?.nome || '');
    };
    const validarCliente = (id) => {
        if (!id) return selecionarCliente(null);
        const cliente = clientes.find((item) => Number(item.id) === Number(id));
        if (cliente) return selecionarCliente(cliente);
        showMessage('Cliente não encontrado!', 'warning');
        selecionarCliente(null);
    };
    const alternarSelecao = (recebimentoId) => setSelecionados([recebimentoId]);
    const recebimentoSelecionado = recebimentos.find((recebimento) => selecionados.includes(recebimento.id));

    const recebimentosOrdenados = useMemo(() => {
        const { campo, direcao } = ordenacao;
        const multiplicador = direcao === 'asc' ? 1 : -1;
        return [...recebimentos].sort((a, b) => {
            const especiais = {
                documentos: [obterDescricaoDocumentos(a), obterDescricaoDocumentos(b)],
                statusRec: [obterDescricaoStatusRecebimento(a.statusRec), obterDescricaoStatusRecebimento(b.statusRec)],
            };
            const valorA = especiais[campo]?.[0] ?? a[campo];
            const valorB = especiais[campo]?.[1] ?? b[campo];
            if (['id', 'quantidadeParcelas', 'valorTotal', 'valorPago', 'saldo', 'diasAtraso'].includes(campo))
                return (Number(valorA || 0) - Number(valorB || 0)) * multiplicador;
            if (['dataCriacao', 'proximoVencimento'].includes(campo))
                return (new Date(valorA || 0) - new Date(valorB || 0)) * multiplicador;
            return String(valorA || '').localeCompare(String(valorB || ''), 'pt-BR', { sensitivity: 'base' }) * multiplicador;
        });
    }, [recebimentos, ordenacao]);
    const alterarOrdenacao = (campo) => setOrdenacao((atual) => ({
        campo,
        direcao: atual.campo === campo && atual.direcao === 'asc' ? 'desc' : 'asc',
    }));
    const cabecalho = (campo, titulo, alinhamento = 'left') => (
        <button type="button" className="grid-sort-button" onClick={() => alterarOrdenacao(campo)} style={{ textAlign: alinhamento }}>
            {titulo} {ordenacao.campo === campo && (ordenacao.direcao === 'asc' ? <FaSortUp /> : <FaSortDown />)}
        </button>
    );
    const exigirUmSelecionado = (acao) => {
        if (selecionados.length !== 1) return showMessage('Selecione exatamente um recebimento.', 'warning');
        acao(selecionados[0]);
    };

    const confirmarExclusao = async (recebimentoId, justificativa) => {
        await excluirRecebimento(recebimentoId, justificativa);
        setSelecionados([]);
        setRecebimentoExclusao(null);
        await buscarRecebimentos(false);
    };

    const imprimirComprovante = async (parcelas) => {
        if (!parcelas?.length) return showMessage('O recebimento não possui pagamentos para imprimir.', 'warning');
        try {
            const pdf = await buscarComprovanteRecebimento(parcelas.map((parcela) => parcela.id));
            await imprimirPdfBlob(pdf, {
                frameId: 'comprovante-recebimento-pdf-print-frame',
                titulo: 'Comprovante de pagamento',
                mensagemErro: 'Não foi possível abrir a impressão do comprovante.',
            });
        } catch (error) {
            showMessage(error?.message || 'Erro ao gerar o comprovante.', 'danger');
        }
    };
    const imprimirSelecionados = async () => {
        if (!selecionados.length) return showMessage('Selecione ao menos um recebimento.', 'warning');
        try {
            const detalhes = await Promise.all(selecionados.map(buscarDetalhesRecebimento));
            const parcelas = detalhes.flatMap((recebimento) => recebimento.recebimentoParcelas)
                .filter((parcela) => Number(parcela.valorPago) > 0);
            await imprimirComprovante(parcelas);
        } catch (error) {
            showMessage(error?.message || 'Não foi possível carregar os pagamentos.', 'danger');
        }
    };

    // Cálculo dos totais
    const totalGrid = recebimentos.reduce((total, recebimento) => total + Number(recebimento.valorTotal || 0), 0);
    const totalPagoGrid = recebimentos.reduce((total, recebimento) => total + Number(recebimento.valorPago || 0), 0);

    return (
        <PageContainer>
            <h2 className="page-title">Contas a Receber</h2>
            <PageHeader>
                <div className="d-flex gap-2 align-items-end">
                    <Input label="Cliente" value={clienteId} size={90}
                        onChange={(event) => setClienteId(Number(event.target.value) || '')}
                        onBlur={(event) => validarCliente(event.target.value)}
                        btnBuscar={async () => selecionarCliente(await showClienteSearch())} />
                    <Input label="Nome do cliente" value={clienteNome} size={250} disabled />
                </div>
                <Select label="Status" value={statusRec} size={150} onChange={(event) => setStatusRec(Number(event.target.value))}
                    options={[
                        { value: STATUS_RECEBIMENTO.TODOS, label: 'Todos' },
                        { value: STATUS_RECEBIMENTO.PAGO, label: 'Pago' },
                        { value: STATUS_RECEBIMENTO.NAO_PAGO, label: 'Não Pago' },
                        { value: STATUS_RECEBIMENTO.PARCIALMENTE_PAGO, label: 'Parcialmente Pago' },
                        { value: STATUS_RECEBIMENTO.PENDENTE, label: 'Pendente' },
                        { value: STATUS_RECEBIMENTO.CANCELADO, label: 'Cancelado' },
                    ]} />
                <Input label="Data inicial" type="date" size={125} value={periodoInicial} onChange={(event) => setPeriodoInicial(event.target.value)} />
                <Input label="Data final" type="date" size={125} value={periodoFinal} onChange={(event) => setPeriodoFinal(event.target.value)} />
                <Select label="Forma prevista" value={formaPagId} size={170} onChange={(event) => setFormaPagId(Number(event.target.value) || '')}
                    options={[{ value: '', label: 'Todas' }, ...formasPag.filter((forma) => !forma.inativo).map((forma) => ({ value: forma.id, label: forma.descricao }))]} />
                <Input label="Nº documento" size={110} value={numeroDocumento} onChange={(event) => setNumeroDocumento(event.target.value)} />
                <button type="button" className="btn btn-search" onClick={() => buscarRecebimentos()}>Buscar</button>
                <div className="relatorios-acoes">
                    <button type="button" className="btn btn-outline-danger"
                        title="Abrir relatório de contas a receber em PDF"
                        disabled={!!gerandoRelatorio || recebimentos.length === 0}
                        onClick={() => gerarRelatorio('pdf')}>
                        <FaFilePdf /> {gerandoRelatorio === 'pdf' ? 'Gerando...' : 'PDF'}
                    </button>
                    <button type="button" className="btn btn-outline-success"
                        title="Baixar relatório de contas a receber em Excel"
                        disabled={!!gerandoRelatorio || recebimentos.length === 0}
                        onClick={() => gerarRelatorio('excel')}>
                        <FaFileExcel /> {gerandoRelatorio === 'excel' ? 'Gerando...' : 'Excel'}
                    </button>
                </div>
            </PageHeader>

            <PageContent>
                <table className="page-content-table recebimentos-grid">
                    <thead><tr>
                        <th></th><th>{cabecalho('id', 'ID')}</th><th>{cabecalho('documentos', 'Documento')}</th><th>{cabecalho('clienteNome', 'Cliente')}</th>
                        <th>{cabecalho('dataCriacao', 'Emissão')}</th><th>{cabecalho('formaPagamentoDescricao', 'Forma prevista')}</th><th>{cabecalho('statusRec', 'Status')}</th>
                        <th>{cabecalho('quantidadeParcelas', 'Parcelas', 'center')}</th><th>{cabecalho('valorTotal', 'Valor total', 'right')}</th><th>{cabecalho('valorPago', 'Valor pago', 'right')}</th>
                        <th>{cabecalho('saldo', 'Saldo', 'right')}</th><th>{cabecalho('proximoVencimento', 'Próx. vencimento')}</th><th>{cabecalho('diasAtraso', 'Atraso', 'center')}</th>
                    </tr></thead>
                    <tbody>
                        {recebimentosOrdenados.map((recebimento) => (
                            <tr key={recebimento.id} onDoubleClick={() => setRecebimentoDetalhesId(recebimento.id)}
                                className={`${recebimento.diasAtraso > 0 ? 'atraso' : ''} ${recebimento.statusRec === 1 ? 'fatura-paga' : ''} ${recebimento.statusRec === 2 ? 'fatura-parcial' : ''}`}>
                                <td><input type="radio" name="recebimentoSelecionado" checked={selecionados.includes(recebimento.id)} onChange={() => alternarSelecao(recebimento.id)} /></td>
                                <td className="fw-bold">{recebimento.id}</td><td>{obterDescricaoDocumentos(recebimento)}</td><td>{recebimento.clienteNome || '-'}</td>
                                <td>{formatDate(recebimento.dataCriacao)}</td><td>{recebimento.formaPagamentoDescricao || '-'}</td>
                                <td><span className={`status-badge ${obterClasseStatusRecebimento(recebimento.statusRec)}`}>{obterDescricaoStatusRecebimento(recebimento.statusRec)}</span></td>
                                <td className="text-center">{recebimento.quantidadeParcelasPagas}/{recebimento.quantidadeParcelas}</td>
                                <td className="text-end">{formatCurrency(recebimento.valorTotal)}</td><td className="text-end text-success">{formatCurrency(recebimento.valorPago)}</td>
                                <td className="text-end fw-bold">{formatCurrency(recebimento.saldo)}</td><td>{recebimento.proximoVencimento ? formatDate(recebimento.proximoVencimento) : '-'}</td>
                                <td className="text-center">{recebimento.diasAtraso > 0 ? `${recebimento.diasAtraso} dia(s)` : '-'}</td>
                            </tr>
                        ))}
                        {!recebimentosOrdenados.length && <tr><td colSpan="13" className="text-center text-muted py-4">Nenhum recebimento encontrado para os filtros informados.</td></tr>}
                    </tbody>
                </table>
            </PageContent>

            <PageFooter>
                <div className="recebimentos-acoes-footer">
                    <button className="btn btn-success" onClick={() => setShowRecebimentoCad(true)}>Incluir recebimento</button>
                    <button className="btn btn-info" onClick={imprimirSelecionados}>Imprimir comprovante</button>
                    <button className="btn btn-secondary" onClick={() => exigirUmSelecionado(setRecebimentoNotasId)}>Visualizar notas</button>
                    <button className="btn btn-primary" onClick={() => exigirUmSelecionado(setRecebimentoDetalhesId)}>Realizar pagamento</button>
                    <button type="button" className="btn btn-outline-danger" disabled={!recebimentoSelecionado}
                        title={recebimentoSelecionado ? 'Excluir o recebimento selecionado' : 'Selecione um recebimento para excluir'}
                        onClick={() => setRecebimentoExclusao(recebimentoSelecionado)}>
                        <FaTrashAlt /> Excluir conta
                    </button>
                </div>
                <div className="recebimentos-totais">
                    <span>Pago: <strong className="text-success">{formatCurrency(totalPagoGrid)}</strong></span>
                    <span>Total: <strong>{formatCurrency(totalGrid)}</strong></span>
                </div>
            </PageFooter>

            <RecebimentoDetalhesModal show={!!recebimentoDetalhesId} recebimentoId={recebimentoDetalhesId}
                onHide={() => setRecebimentoDetalhesId(null)} onAlterado={() => buscarRecebimentos(false)} onImprimir={imprimirComprovante} />
            <RecebimentoNotasModal show={!!recebimentoNotasId} recebimentoId={recebimentoNotasId} onHide={() => setRecebimentoNotasId(null)} />
            <RecebimentoCad show={showRecebimentoCad} onHide={() => setShowRecebimentoCad(false)} onSave={() => buscarRecebimentos()} />
            <RecebimentoExclusaoModal show={!!recebimentoExclusao} recebimento={recebimentoExclusao}
                onHide={() => setRecebimentoExclusao(null)} onConfirm={confirmarExclusao} />
        </PageContainer>
    );
};

export default RecebimentoConsultaPage;
